import React, { useEffect, useState } from 'react';
import styled from 'styled-components';
import Footer from '../components/Footer';
import InnerPageHeader from '../components/InnerPageHeader';
import {
  BulletItem,
  BulletList,
  Emphasis,
  InnerCard,
  InnerContainer,
  InnerSection,
  PageTitle,
  Paragraph,
  SectionTitle,
  TextLink,
} from '../components/InnerPageLayout';
import JsonData from '../data/data.json';
import { SiteData } from '../types/siteDataTypes';
import logoBlack from '../assets/images/logo_black.png';

const CardStack = styled.div`
  display: grid;
  gap: 24px;
`;

const ButtonSection = styled.div`
  margin-bottom: 65px;
`;

const ButtonRow = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 14px;
  justify-content: center;
`;

const ProductButton = styled.a<{ accent: string }>`
  display: inline-flex;
  align-items: center;
  gap: 10px;
  padding: 10px 18px;
  margin: -20px 20px;
  border-radius: 12px;
  border: 2px solid ${({ accent }) => accent};
  background: ${({ accent }) => accent};
  color: #000000;
  text-decoration: none;
  font-weight: 500;
  font-size: 16px;
  line-height: 24px;
  box-shadow: rgba(29, 41, 61, 0.02) 0 1px 0.5px 0.05px;
  transition: transform 0.3s ease, box-shadow 0.3s ease;

  &:hover {
    transform: translateY(-2px);
    box-shadow: 0 8px 20px rgba(12, 28, 45, 0.15);
    color: #000000;
  }
`;

const ProductLogo = styled.img`
  width: 24px;
  height: 24px;
  object-fit: contain;
  filter: drop-shadow(0 2px 6px rgba(0, 0, 0, 0.2));
`;

const Products: React.FC = () => {
  const [siteData, setSiteData] = useState<SiteData | null>(null);

  useEffect(() => {
    setSiteData(JsonData as SiteData);
  }, []);

  const chotBuddiesDownload =
    siteData?.ChotBuddiesHomeHeader?.primaryCtaHref || 'https://play.google.com';
  const chotBuddiesAppStore =
    siteData?.ChotBuddiesHomeHeader?.secondaryCtaHref || 'https://apps.apple.com';

  return (
    <>
      <InnerPageHeader
        data={{
          title: 'Products',
          paragraph:
            'Focused AI applications built around real human needs and clear outcomes.',
        }}
      />
      <ButtonSection>
            <ButtonRow>
              <ProductButton href="/products/chot-buddies/" accent="#817ffd">
                <ProductLogo src={logoBlack} alt="Chot Buddies logo" />
                Chot Buddies
              </ProductButton>
              <ProductButton href="/products/chot-study/" accent="#1a759f">
                <ProductLogo src={logoBlack} alt="Chot Study logo" />
                Chot Study
              </ProductButton>
            </ButtonRow>
          </ButtonSection>
      <InnerSection>
        <InnerContainer>
          <CardStack>
            <InnerCard>
              <PageTitle>Product Philosophy</PageTitle>
              <Paragraph>
                Chots builds focused AI products designed around real human needs.
              </Paragraph>
              <Paragraph>
                Each product solves a specific problem, has a clear audience, and is built with the
                same principles: simplicity, privacy, and thoughtful design.
              </Paragraph>
              <Paragraph>We don't believe in one-size-fits-all AI tools.</Paragraph>
              <Paragraph>
                Instead, we build <Emphasis>purpose-built applications</Emphasis>, each designed to
                do one thing well - so users don't need to learn AI to benefit from it.
              </Paragraph>
              <Paragraph>Our products are:</Paragraph>
              <BulletList>
                <BulletItem>Designed before they are powered by AI.</BulletItem>
                <BulletItem>Opinionated to reduce cognitive load.</BulletItem>
                <BulletItem>Built to be trusted with personal data.</BulletItem>
              </BulletList>
            </InnerCard>

            <InnerCard>
              <PageTitle>Shared Principles Across All Products</PageTitle>
              <Paragraph>
                Every Chots product is built with the same core commitments:
              </Paragraph>
              <BulletList>
                <BulletItem>
                  <Emphasis>Simplicity:</Emphasis> Clear experiences without unnecessary
                  configuration.
                </BulletItem>
                <BulletItem>
                  <Emphasis>Focus:</Emphasis> One product, one primary problem.
                </BulletItem>
                <BulletItem>
                  <Emphasis>Privacy by Design:</Emphasis> Encrypted data and strict access
                  boundaries.
                </BulletItem>
                <BulletItem>
                  <Emphasis>Responsibility:</Emphasis> Thoughtful use of AI where it truly adds
                  value.
                </BulletItem>
              </BulletList>
              <Paragraph>These principles guide every product decision we make.</Paragraph>
            </InnerCard>

            <InnerCard>
              <PageTitle>Chot Buddies</PageTitle>
              <Paragraph>
                <Emphasis>Private AI companions for everyday life</Emphasis>
              </Paragraph>
              <Paragraph>
                Chot Buddies is a mobile app that gives users access to specialized AI assistants,
                each designed to support a specific area of life.
              </Paragraph>
              <Paragraph>
                Rather than a single generic chatbot, Chot Buddies offers a set of focused
                companions, each with a clear role, tone, and expertise.
              </Paragraph>

              <SectionTitle>What it's used for</SectionTitle>
              <BulletList>
                <BulletItem>Personal growth and self-reflection.</BulletItem>
                <BulletItem>Productivity and life organization.</BulletItem>
                <BulletItem>Learning and skill development.</BulletItem>
                <BulletItem>Creativity and brainstorming.</BulletItem>
                <BulletItem>Everyday decision support.</BulletItem>
              </BulletList>

              <SectionTitle>What makes it different</SectionTitle>
              <BulletList>
                <BulletItem>Multiple domain-focused AI assistants.</BulletItem>
                <BulletItem>Deep personalization using encrypted personal context.</BulletItem>
                <BulletItem>Conversations designed for daily, ongoing use.</BulletItem>
                <BulletItem>Strong privacy guarantees by design.</BulletItem>
              </BulletList>

              <SectionTitle>Availability</SectionTitle>
              <BulletList>
                <BulletItem>Android - available now.</BulletItem>
                <BulletItem>iOS - available now.</BulletItem>
                <BulletItem>Web - planned.</BulletItem>
              </BulletList>
              <Paragraph>
                <TextLink href="/products/chot-buddies/">View Chot Buddies</TextLink> &nbsp;|&nbsp;{' '}
                <TextLink href={chotBuddiesDownload}>Download on Google Play</TextLink> &nbsp;|&nbsp;{' '}
                <TextLink href={chotBuddiesAppStore}>Download on the App Store</TextLink>
              </Paragraph>
            </InnerCard>

            <InnerCard>
              <PageTitle>Chot Study</PageTitle>
              <Paragraph>
                <Emphasis>Turn your study materials into interactive learning</Emphasis>
              </Paragraph>
              <Paragraph>
                Chot Study is an AI-powered learning app designed for students and lifelong learners
                who want to actively learn from their own materials.
              </Paragraph>
              <Paragraph>
                Instead of passively rereading notes or documents, Chot Study transforms content into
                interactive learning experiences.
              </Paragraph>

              <SectionTitle>What it's used for</SectionTitle>
              <BulletList>
                <BulletItem>Studying from PDFs, notes, and presentations.</BulletItem>
                <BulletItem>Organizing learning by subjects and topics.</BulletItem>
                <BulletItem>Practicing recall through AI-generated questions.</BulletItem>
                <BulletItem>Learning through games and structured repetition.</BulletItem>
              </BulletList>

              <SectionTitle>Core idea</SectionTitle>
              <Paragraph>
                Your documents become the foundation for personalized learning - questions, games,
                and insights generated directly from your own content.
              </Paragraph>

              <SectionTitle>Key principles</SectionTitle>
              <BulletList>
                <BulletItem>Active learning over passive reading.</BulletItem>
                <BulletItem>Structure without rigidity.</BulletItem>
                <BulletItem>Clear progress and feedback.</BulletItem>
              </BulletList>

              <SectionTitle>Availability</SectionTitle>
              <BulletList>
                <BulletItem>In active development.</BulletItem>
                <BulletItem>Launching soon.</BulletItem>
              </BulletList>
              <Paragraph>
                <TextLink href="/products/chot-study/">Learn more about Chot Study</TextLink>
              </Paragraph>
            </InnerCard>

            <InnerCard>
              <PageTitle>What's Next</PageTitle>
              <Paragraph>
                Chots.AI is building a growing ecosystem of AI products, each designed around a
                specific real-world use case.
              </Paragraph>
              <Paragraph>
                As the company grows, new products will be added - carefully, intentionally, and
                with the same standards applied to everything we build.
              </Paragraph>
            </InnerCard>
          </CardStack>
        </InnerContainer>
      </InnerSection>
      <Footer />
    </>
  );
};

export default Products;
